/**
 * @type Feature (Security)
 * @rationale Single source of truth for redacting credentials out of anything that
 *   gets forwarded to the console-error telemetry endpoint. Both the axios and the
 *   window.fetch interceptors, and the forwarder's own send path, use this so there
 *   is no shallow-redaction gap (id_token / access_token / nested tokens leaked
 *   through the old per-interceptor copies).
 */

// Redact any key that looks like a credential, at ANY nesting depth.
// `assertion` covers assertion/client_assertion; `saml` covers saml_response.
// Deliberately NOT matching bare `code` — that over-redacts errorCode/statusCode/
// postal code; the code/code_verifier leak is handled by the auth-body skip instead.
const SENSITIVE_KEY_PATTERN = /token|password|secret|api[_-]?key|authorization|credential|assertion|saml/i;

// A form-urlencoded string: &-separated `key=value` pairs (bare flags allowed),
// no whitespace. Real form bodies percent-encode spaces, so requiring no
// whitespace cleanly rejects free text ("Internal Server Error", stack lines),
// which must keep passing through un-mangled.
const FORM_ENCODED_PATTERN = /^[^=&\s]+(=[^&\s]*)?(&[^=&\s]+(=[^&\s]*)?)*$/;

// A path segment that looks like a bearer token: 12+ chars of pure
// alphanumerics. The public availability-link tokens are exactly this shape
// (secrets.choice over [0-9A-Za-z], length 12, riding in /a/<token> and
// /api/driver-availability-links/<token>). Real route words in this app are
// shorter or contain hyphens, so false positives are rare — and when one does
// occur the cost is an over-redacted path segment, never a leak.
const TOKEN_SEGMENT_PATTERN = /^[0-9A-Za-z]{12,}$/;

function redactPathTokens(path: string): string {
  return path
    .split('/')
    .map((segment) => (TOKEN_SEGMENT_PATTERN.test(segment) ? '***REDACTED***' : segment))
    .join('/');
}

// Secret shapes that are unmistakable inside FREE TEXT (error messages, stack
// traces). Free text has no key/value structure to redact by, so only patterns
// that cannot plausibly be ordinary prose are replaced. A bare secret with no
// surrounding context is indistinguishable from a hash/request-id/element-id
// and is deliberately NOT matched — that residual risk is irreducible in any
// telemetry system and is mitigated by the admin-only read gate.
const JWT_PATTERN = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;
const AUTH_SCHEME_PATTERN = /\b(Bearer|Basic)\s+[A-Za-z0-9._~+/=-]{8,}/gi;
// `id_token=abc`, `password: xyz` … — credential-like key, =/: separator, value.
const KEYED_SECRET_PATTERN =
  /([\w-]*(?:token|password|secret|api[_-]?key|authorization|credential|assertion|saml)[\w-]*["']?\s*[=:]\s*["']?)[^\s"'&,;)\]}]+/gi;
const URL_IN_TEXT_PATTERN = /https?:\/\/[^\s"'<>)\]}]+/g;

/**
 * Redact unmistakable secret shapes from free text (messages, stacks) while
 * leaving prose readable. URLs embedded in the text are routed through
 * sanitizeUrl so their query/path redaction stays consistent.
 */
export function sanitizeText(text: string | undefined | null): string | undefined {
  if (typeof text !== 'string' || text.length === 0) return undefined;
  return text
    .replace(URL_IN_TEXT_PATTERN, (match) => sanitizeUrl(match) ?? match)
    .replace(JWT_PATTERN, '***REDACTED***')
    .replace(AUTH_SCHEME_PATTERN, '$1 ***REDACTED***')
    .replace(KEYED_SECRET_PATTERN, '$1***REDACTED***');
}

/** Redact the value of every `key=value` pair in a query/form-encoded string. */
function redactPairValues(query: string): string {
  return query
    .split('&')
    .map((pair) => {
      if (pair.length === 0) return pair;
      const eq = pair.indexOf('=');
      if (eq === -1) return pair; // bare flag — no value to leak
      return `${pair.slice(0, eq)}=***REDACTED***`;
    })
    .join('&');
}

export function sanitizeData(data: unknown, depth = 0): unknown {
  if (data === null || data === undefined) return undefined;
  if (depth > 6) return '***TRUNCATED***'; // guard against deep / cyclic structures

  if (typeof data === 'string') {
    // fetch() bodies are usually JSON strings; parse so nested credentials are
    // redacted rather than shipped verbatim.
    try {
      const parsed = JSON.parse(data);
      if (parsed && typeof parsed === 'object') {
        return sanitizeData(parsed, depth + 1);
      }
    } catch {
      // not JSON — fall through
    }
    // Form-urlencoded strings (`token=…&code=…`) are the query string in body
    // form — the classic OAuth credential carrier — and would otherwise bypass
    // key-based redaction entirely (the string is never parsed into keys). Give
    // them the same value-based redaction sanitizeUrl applies to URL queries.
    // Free-text strings (error messages, plain-text bodies) still pass through.
    if (data.includes('=') && FORM_ENCODED_PATTERN.test(data)) {
      return redactPairValues(data);
    }
    return data;
  }

  if (Array.isArray(data)) {
    return data.map((item) => sanitizeData(item, depth + 1));
  }

  // Non-plain objects (FormData/Blob/File/ArrayBuffer/class instances) must be
  // DESCRIBED rather than run through Object.entries(), which blanks them to `{}`
  // and silently drops a multipart/binary body. Never read file contents.
  if (typeof data === 'object') {
    // Tag a Blob/File value without reading its bytes.
    const tagBinary = (value: unknown): string | null => {
      if (typeof File !== 'undefined' && value instanceof File) {
        return `[File ${value.name ?? ''} ${value.size}b]`;
      }
      if (typeof Blob !== 'undefined' && value instanceof Blob) {
        return `[Blob ${value.size}b]`;
      }
      return null;
    };

    // URLSearchParams IS a query string in object form — a secret can ride
    // under a benign key (`code`, `sig`) the key pattern won't match, so
    // value-redact every entry (mirrors sanitizeUrl/sanitizeParams), don't
    // recurse into key-based redaction.
    if (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams) {
      const obj: Record<string, unknown> = {};
      for (const [key] of data.entries()) obj[key] = '***REDACTED***';
      return obj;
    }

    // FormData: iterable via .entries(). Convert to a plain object (tagging
    // binary values) and recurse so keys still get redacted. Key-based is right
    // here — multipart bodies are file uploads plus benign fields that should
    // stay readable for debugging.
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      const obj: Record<string, unknown> = {};
      for (const [key, value] of data.entries()) {
        obj[key] = tagBinary(value) ?? value;
      }
      return sanitizeData(obj, depth + 1);
    }

    // Top-level Blob/File: size/type tag string (never read contents).
    const binaryTag = tagBinary(data);
    if (binaryTag) return binaryTag;

    // Top-level ArrayBuffer: size tag.
    if (typeof ArrayBuffer !== 'undefined' && data instanceof ArrayBuffer) {
      return `[ArrayBuffer ${data.byteLength}b]`;
    }

    // Only treat true plain objects (Object.prototype / null prototype) as records.
    const proto = Object.getPrototypeOf(data);
    if (proto !== Object.prototype && proto !== null) {
      return `[${(data as { constructor?: { name?: string } }).constructor?.name ?? 'Object'}]`;
    }
  }

  if (typeof data === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
      out[key] = SENSITIVE_KEY_PATTERN.test(key)
        ? '***REDACTED***'
        : sanitizeData(value, depth + 1);
    }
    return out;
  }

  return data;
}

/**
 * Redact secrets that ride inside a URL before it is forwarded to telemetry.
 *
 * Query-string *values* (`?code=…`, `?access_token=…`, `?sig=…`, or any unknown
 * secret) and the entire fragment (`#access_token=…` from an OAuth implicit
 * redirect) are replaced; the origin/path and the query *keys* are kept so the
 * URL stays useful for debugging.
 *
 * Unlike sanitizeData, this redacts values regardless of key name — an OAuth
 * `code` in a URL is a credential even though `code` is deliberately absent from
 * SENSITIVE_KEY_PATTERN (it over-matches errorCode/statusCode as an object key).
 * Never throws; works on absolute or relative URLs and passes non-URLs through.
 */
export function sanitizeUrl(url: string | undefined | null): string | undefined {
  if (typeof url !== 'string' || url.length === 0) return undefined;

  // Drop the fragment entirely — implicit-flow tokens live in `#access_token=…`.
  const hashIndex = url.indexOf('#');
  const withoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex);

  const queryIndex = withoutHash.indexOf('?');
  const base = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex);

  // Bearer tokens also ride in the PATH, not just the query — e.g. the public
  // availability-link routes. Redact token-shaped segments, but never touch the
  // scheme/host of an absolute URL.
  const schemeEnd = base.indexOf('://');
  let safeBase: string;
  if (schemeEnd === -1) {
    safeBase = redactPathTokens(base); // relative URL — all of it is path
  } else {
    const pathStart = base.indexOf('/', schemeEnd + 3);
    safeBase =
      pathStart === -1
        ? base // absolute URL with no path
        : base.slice(0, pathStart) + redactPathTokens(base.slice(pathStart));
  }

  if (queryIndex === -1) return safeBase;
  const query = withoutHash.slice(queryIndex + 1);
  if (query.length === 0) return safeBase;

  return `${safeBase}?${redactPairValues(query)}`;
}

/**
 * Redact the VALUES of forwarded query parameters, keeping keys and non-string
 * scalars.
 *
 * Query params are the URL query string in structured form, and a secret can
 * ride under a benign-looking key that the key-based SENSITIVE_KEY_PATTERN will
 * NOT match — e.g. `?code=<oauth-code>` or `?sig=<signature>` (`code`/`sig` are
 * deliberately not in the pattern). So, mirroring sanitizeUrl's distrust of the
 * query string, every STRING value is redacted (credentials are strings —
 * tokens, codes, signatures, passwords), while numbers/booleans/null are kept
 * (driver ids, week numbers, flags — identifiers forwarded for debugging, which
 * cannot be credentials). Full values remain recoverable from server logs via
 * the request id. Use this for params specifically; use sanitizeData for bodies,
 * where benign fields should stay readable.
 */
export function sanitizeParams(params: unknown): unknown {
  const redact = (value: unknown): unknown => {
    if (typeof value === 'string') return '***REDACTED***';
    // URLSearchParams: value-redact each entry (do NOT route through sanitizeData,
    // which is key-based and would let ?code=… through).
    if (typeof URLSearchParams !== 'undefined' && value instanceof URLSearchParams) {
      const out: Record<string, unknown> = {};
      for (const [key] of value.entries()) out[key] = '***REDACTED***';
      return out;
    }
    if (Array.isArray(value)) return value.map(redact);
    if (value && typeof value === 'object') {
      // Unknown non-plain object (Blob/File/etc.) — describe, never read bytes.
      const proto = Object.getPrototypeOf(value);
      if (proto !== Object.prototype && proto !== null) return sanitizeData(value);
      const out: Record<string, unknown> = {};
      for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
        out[key] = redact(v);
      }
      return out;
    }
    return value; // number, boolean, null, undefined — kept
  };
  return redact(params);
}
